/**
 * Performs a naive equality check between two objects, including array values.
 * Treats empty arrays and falsy values as equal.
 * Treats empty strings and falsy values as equal.
 * Recursively compares nested object values (not null, not array).
 *
 * @typeParam T - The type of the objects being compared.
 * @param a - The first object to compare.
 * @param b - The second object to compare.
 * @returns True if all properties (including arrays) are equal, false otherwise.
 *
 * @example
 * ```typescript
 * naiveEqual({ a: 1, b: [2, 3] }, { a: 1, b: [2, 3] }) // true
 * naiveEqual({ a: 1, b: [2, 3] }, { a: 1, b: [2, 4] }) // false
 * naiveEqual({ a: 1, b: { c: 2 } }, { a: 1, b: { c: 2 } }) // true
 * naiveEqual({ a: 1, b: [] }, { a: 1, b: null }) // true
 * naiveEqual({ a: 1, b: null }, { a: 1, b: [] }) // true
 * naiveEqual({ a: new Date(0) }, { a: new Date(1) }) // false
 * ```
 */ export const naiveEqual = (a, b)=>{
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA){
        const valA = a[key];
        const valB = b[key];
        if (Array.isArray(valA) && valA.length === 0 && !valB) continue; // Treat empty arrays and falsy values as equal
        if (Array.isArray(valB) && valB.length === 0 && !valA) continue; // Treat falsy values and empty arrays as equal
        if (Array.isArray(valA) && Array.isArray(valB)) {
            if (valA.length !== valB.length) return false;
            valA.sort();
            valB.sort();
            for(let i = 0; i < valA.length; i++){
                if (valA[i] !== valB[i]) return false;
            }
            continue;
        }
        if (valA === '' && !valB) continue; // Treat empty string and falsy values as equal
        if (valB === '' && !valA) continue; // Treat empty string and falsy values as equal
        // Dates have no enumerable own keys, so the object branch below would consider any two of them equal
        if (valA instanceof Date || valB instanceof Date) {
            if (!(valA instanceof Date) || !(valB instanceof Date)) return false;
            if (valA.getTime() !== valB.getTime()) return false;
            continue;
        }
        if (isObject(valA) || isObject(valB)) {
            if (!isObject(valA) || !isObject(valB)) return false;
            if (!naiveEqual(valA, valB)) return false;
            continue;
        }
        if (valA !== valB) return false;
    }
    return true;
};
const isObject = (value)=>{
    return value !== null && typeof value === 'object' && !Array.isArray(value);
};
/**
 * Maps the values of an object using a provided mapping function, returning a new object with the same keys.
 *
 * @typeParam T - The type of the input object.
 * @typeParam U - The type of the mapped values.
 * @param object - The object whose values will be mapped.
 * @param mapFn - A function that receives each value and key from the object and returns a new value.
 * @returns A new object with the same keys as the input object, but with values mapped by `mapFn`.
 *
 * @example
 * ```typescript
 * const obj = { a: 1, b: 2, c: 3 };
 * const mapped = objectMap(obj, (value, key) => value * 2);
 * // mapped: { a: 2, b: 4, c: 6 }
 * ```
 */ export const objectMap = (object, mapFn)=>{
    return Object.keys(object).reduce((result, key)=>{
        result[key] = mapFn(object[key], key);
        return result;
    }, {});
};
/**
 * Compares two objects and returns a new object containing only the key-value pairs from the first object
 * where the values differ from those in the second object.
 *
 * @typeParam T - The type of the objects being compared.
 * @param a - The first object to compare.
 * @param b - The second object to compare against.
 * @returns A new object containing only the key-value pairs from `a` where the values differ from those in `b`.
 *
 * @example
 * ```typescript
 * const obj1 = { a: 1, b: 2, c: 3 };
 * const obj2 = { a: 1, b: 4, c: 3 };
 * const differences = diffObjects(obj1, obj2);
 * // differences: { b: 2 }
 * ```
 */ export const diffObjectValues = (a, b)=>{
    const result = {};
    for(const key in a){
        if (Object.prototype.hasOwnProperty.call(a, key)) {
            if (a[key] !== b[key]) {
                result[key] = a[key];
            }
        }
    }
    return result;
};
/**
 * Returns a new object containing only the keys present in the template,
 * with values taken from the source object.
 *
 * @param source - The object to pick values from.
 * @param template - An object whose keys define which properties to pick.
 * @returns A new object with the intersection of keys from `template`, valued from `source`.
 *
 * @example
 * ```typescript
 * const full = { a: 1, b: 2, c: 3, d: 4 }
 * const template = { a: null, c: null }
 * intersection(full, template) // { a: 1, c: 3 }
 * ```
 */ export const intersection = (source, template)=>{
    const result = {};
    for(const key in template){
        if (Object.prototype.hasOwnProperty.call(template, key) && Object.prototype.hasOwnProperty.call(source, key)) {
            result[key] = source[key];
        }
    }
    return result;
};
/**
 * Returns a new object containing only the key-value pairs from `values` that differ from `defaults`.
 *
 * @param values - The object containing values to compare against defaults.
 * @param defaults - The object containing default values.
 * @returns A new object containing only the key-value pairs from `values` that differ from `defaults`.
 */ export const nonDefaultPartial = (values, defaults)=>{
    const partial = intersection(values, defaults) // filter out extrenuous keys from overlapping interfaces
    ;
    const complete = {
        ...defaults,
        ...partial
    } // fill in missing keys with defaults
    ;
    const diff = diffObjectValues(complete, defaults) // filter out keys that are the same as defaults
    ;
    return diff;
};
